import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, cn} from '@chekinapp/ui';
import {OAUTH_PROVIDER} from '@chekin/core';
import {useGoogleSignIn} from 'hooks/auth/useGoogleSignIn';
import {useAnalytics, POSTHOG_EVENTS} from 'analytics';
import GoogleIcon from 'assets/icons/google.svg?react';

const BASE_API_URL = import.meta.env.VITE_APP_BASE_URL;

type GoogleSignInButtonProps = {
  onSuccessAuth?: () => void;
  disabled?: boolean;
  className?: string;
};

export const GoogleSignInButton = ({
  onSuccessAuth,
  disabled,
  className,
}: GoogleSignInButtonProps) => {
  const {t} = useTranslation();
  const {subscribeOAuthGoogleCompletion} = useGoogleSignIn();
  const {track} = useAnalytics();

  const handleClick = (provider: OAUTH_PROVIDER) => () => {
    track(POSTHOG_EVENTS.AUTH_STARTED, {method: 'google'});
    const returnOrigin = encodeURIComponent(window.location.origin);
    const popupUrl = `${BASE_API_URL}/api/v1/guest-accounts/oauth/${provider}/?return_origin=${returnOrigin}`;
    window.open(popupUrl, 'google-oauth', 'width=500,height=600');
  };

  useEffect(() => {
    const subscriber = subscribeOAuthGoogleCompletion({
      onSuccess: () => {
        track(POSTHOG_EVENTS.AUTH_SUCCEEDED, {method: 'google'});
        onSuccessAuth?.();
      },
      onError: () =>
        track(POSTHOG_EVENTS.AUTH_FAILED, {method: 'google', reason: 'oauth_error'}),
    });

    return subscriber.unsubscribe;
  }, [onSuccessAuth, subscribeOAuthGoogleCompletion, track]);

  return (
    <Button
      variant="outline"
      onClick={handleClick(OAUTH_PROVIDER.google)}
      disabled={disabled}
      className={cn(
        'h-12 w-full gap-2.5 rounded-xl text-[14px] font-semibold text-[var(--chekin-color-brand-navy)] lg:border-transparent lg:bg-[var(--chekin-color-white)]',
        className,
      )}
    >
      <GoogleIcon width={18} height={18} className="shrink-0" />
      {t('auth.continue_with_google')}
    </Button>
  );
};
